/* eslint-disable react/jsx-no-useless-fragment */
import React, { Fragment, createElement } from 'react';
import classNames from 'classnames';
import {
  func, oneOf, oneOfType, shape, string, bool, number,
} from 'prop-types';
import { Small, ConditionalWrapper } from 'components/common';
import {
  defaultTo, evolve, is, lensProp, over, when,
} from 'ramda';
import { executeFormat } from 'helpers/behaviors';
import { sanitize, isNotNullish } from 'helpers/Utils';

sanitize()(evolve({
  description: when(is(Object), over(lensProp('position'), defaultTo('pre'))),
}));

const showDynamicValue = ({
  description,
  dynamicValue,
  showColumns,
  display,
}) => {
  const setupDescription = (dynamicVal, descriptionItem, showColumnsItem) => {
    if (isNotNullish(dynamicVal)
      || isNotNullish(descriptionItem?.value)
      || descriptionItem?.prefix
      || descriptionItem?.suffix) {
      return (
        <div className={classNames(showColumnsItem, 'input-title-wrapper', 'read-only-description')}>
          <ConditionalWrapper
            condition={display === 'small'}
            wrappingElement={children => <Small secondary>{children}</Small>}
          >
            {executeFormat({
              placeholder: descriptionItem.placeholder,
              value: dynamicVal ?? descriptionItem.value,
              valueFormat: descriptionItem.valueFormat,
              options: {
                prefix: descriptionItem.prefix,
                suffix: descriptionItem.suffix,
                decimalPlaces: descriptionItem.decimalPlaces,
              },
            })}
          </ConditionalWrapper>
        </div>
      );
    }
    return <Fragment />;
  };

  if (Array.isArray(description) && dynamicValue) {
    return (
      <div className={classNames(showColumns, 'input-title-wrapper')}>
        {
          description.map((descriptionItem, index) => {
            const dynamicVal = dynamicValue[index];
            return setupDescription(dynamicVal, descriptionItem);
          })
        }
      </div>
    );
  }
  return setupDescription(dynamicValue, description, showColumns);
};

showDynamicValue.defaultProps = {
  description: {},
  dynamicValue: null,
  showColumns: null,
  display: 'small',
};

showDynamicValue.propTypes = {
  description: shape({}),
  dynamicValue: string,
  showColumns: string,
  display: string,
};

function ReadOnlyField(props) {
  const {
    title,
    subtitle,
    description,
    descriptionShowAsBlock,
    dynamicValue,
    showAsColumns,
    valueDisplayAs,
  } = props;

  const showColumns = showAsColumns?.className;

  return (
    <div className={classNames({ 'layout-row': !!showColumns })}>
      {description?.position === 'pre' && (
        (description?.component && createElement(description.component, props, null))
        || description?.render(props)
      )}
      <div
        className={classNames(
          'input-title-wrapper',
          showColumns,
          { 'input-title-block-wrapper': descriptionShowAsBlock }
        )}
      >
        {(title || subtitle) && (
          <label className="input-title-wrapper">
            {title && <strong>{title}</strong>}
            {subtitle && <Small secondary>{subtitle}</Small>}
          </label>
        )}
      </div>
      {showDynamicValue({
        description: description?.items || description,
        dynamicValue,
        showColumns,
        valueDisplayAs,
      })}
      {description?.position === 'post' && (
        (description?.component && createElement(description.component, props, null))
        || description?.render(props)
      )}
    </div>
  );
}

ReadOnlyField.defaultProps = {
  title: null,
  subtitle: null,
  description: null,
  descriptionShowAsBlock: false,
  dynamicValue: null,
  showAsColumns: {},
  valueDisplayAs: 'small',
};

ReadOnlyField.propTypes = {
  title: string,
  subtitle: string,
  description: oneOfType([
    string,
    shape({
      position: oneOf(['pre', 'post']),
      render: func,
      component: func,
    }),
  ]),
  descriptionShowAsBlock: bool,
  showAsColumns: shape({}),
  dynamicValue: oneOfType([number, string]),
  valueDisplayAs: string,
};

export default ReadOnlyField;
